import moment from 'moment'
import isEmpty from 'lodash/isEmpty'
import { Intent } from '@blueprintjs/core'
import { dispatchErrorToast } from 'src/store/modules/utils/toast'
import { createReducer, TIMESTAMP_FORMAT } from 'src/utils/reducer'
import { SOLUTIONS_URL } from 'src/constants/urls'
import * as actionTypes from 'src/constants/actionTypes'
import ActionsHelper, { initialActionState } from './ActionsHelper'
import { getRunbook } from '..'

export const BULK_UPDATE_ACTIONS = 'BULK_UPDATE_ACTIONS'

export const PENDING = 0
export const IN_PROGRESS = 1
export const READY_FOR_EXECUTION = 2
export const SUCCESS = 4
export const FAILED = 5

export const statusOptions = {
    byId: {
        [PENDING]: {
            value: 'Pending',
            onSelect: 'reset',
            bgColor: '#9AA9B3',
        },
        [READY_FOR_EXECUTION]: {
            value: 'Ready for Execution',
            onSelect: 'ready_for_execution',
            bgColor: '#007CAD',
        },
        [IN_PROGRESS]: {
            value: 'In Progress',
            onSelect: 'start',
            bgColor: '#FFCE47',
        },
        [SUCCESS]: {
            value: 'Success',
            onSelect: 'success',
            bgColor: '#15B371',
        },
        [FAILED]: {
            value: 'Failed',
            onSelect: 'failed',
            bgColor: '#E74A5A',
        },
    },
    ids: [PENDING, READY_FOR_EXECUTION, IN_PROGRESS, SUCCESS, FAILED],
}

const genericActionUpdate = (state, action) => ({
    ...state,
    ...action.data,
})

const getAffectedActions = (uid, byId, force) => {
    const ids = []
    const action = byId[uid]
    for (let child of action.treeChildren) {
        ids.push(child)
        if (byId[child].expanded || force) {
            ids.push(...getAffectedActions(child, byId, force))
        }
    }
    return ids
}

/**
 * Checks if a parent action should be auto-collapsed based on all children being successful
 * @param {number} uid - The action uid to check
 * @param {Object} byId - The actions map
 * @returns {boolean} - True if action should be auto-collapsed
 */
const shouldAutoCollapse = (uid, byId) => {
    const action = byId[uid]

    // Don't collapse if:
    // - Action doesn't exist
    // - Action has no tree children
    if (!action || !action.treeChildren || action.treeChildren.length === 0) {
        return false
    }

    // Get visible children (not filtered)
    const visibleChildren = action.treeChildren.filter((childUid) => {
        const child = byId[childUid]
        return child && !child.filteredByUser && !child.filteredByAction
    })

    // If no visible children, don't collapse
    if (visibleChildren.length === 0) {
        return false
    }

    // Check if all visible tree children have status SUCCESS (4)
    const allSuccessful = visibleChildren.every(
        (childUid) => byId[childUid] && byId[childUid].status === SUCCESS
    )

    return allSuccessful
}

/**
 * Recursively applies auto-collapse logic to parent actions when all children are successful
 * Only runs during active session (execution mode)
 * @param {Array} updatedActionUids - UIDs of actions that were just updated
 * @param {Object} byId - The actions map (will be mutated)
 * @param {boolean} hasActiveSession - Whether a session is currently active
 */
const applyAutoCollapse = (updatedActionUids, byId, hasActiveSession) => {
    // Only auto-collapse during active session (execution mode)
    if (!hasActiveSession) {
        return
    }

    // Track which actions we've already processed in this update cycle
    const processed = new Set()

    // Collect unique parent UIDs from all updated actions that became successful
    // Also check the updated actions themselves if they have children (in case parent was manually completed)
    const parentsToCheck = new Set()
    updatedActionUids.forEach((uid) => {
        const action = byId[uid]
        if (action && action.status === SUCCESS) {
            // Check if this action itself should be collapsed (has children that are all successful)
            if (action.treeChildren && action.treeChildren.length > 0) {
                parentsToCheck.add(uid)
            }
            // Also check the parent of this action
            if (action.treeParent) {
                parentsToCheck.add(action.treeParent)
            }
        }
    })

    // Recursively check and collapse parents
    const collapseIfAllChildrenSuccessful = (uid) => {
        // Skip if already processed in this cycle
        if (processed.has(uid)) {
            return
        }
        processed.add(uid)

        const parent = byId[uid]

        if (!parent) {
            return
        }

        // Skip if already collapsed - prevents toggling
        if (!parent.expanded) {
            return
        }

        // Check if this parent should be collapsed
        const shouldCollapse = shouldAutoCollapse(uid, byId)

        if (shouldCollapse) {
            // Collapse the parent
            parent.expanded = false

            // Hide all descendant actions
            const affectedActions = getAffectedActions(uid, byId, true)
            affectedActions.forEach((descendantUid) => {
                byId[descendantUid].visible = false
            })

            // Recursively check the parent's parent
            if (parent.treeParent) {
                collapseIfAllChildrenSuccessful(parent.treeParent)
            }
        }
    }

    // Process all parents
    parentsToCheck.forEach((parentUid) => {
        collapseIfAllChildrenSuccessful(parentUid)
    })
}

const runbookAction = createReducer(initialActionState, {
    [actionTypes.EXPAND_ACTION]: (state) => ({
        ...state,
        expanded: !state.expanded,
    }),
    [actionTypes.ADD_NEW_ACTION]: genericActionUpdate,
    [actionTypes.UPDATE_ACTION]: genericActionUpdate,
    [actionTypes.SAVE_SUPPLIER_SUCCESS]: (state, action) => {
        return {
            ...state,
            ownerSupplierId: action.payload.data.id,
            ownerSupplier: action.payload.data.email,
        }
    },
    [actionTypes.TOGGLE_FREEZE_OWNER]: (state, action) => {
        const freezeOwnerTeam = state.freezeOwnerTeam === 0 ? 1 : 0
        return {
            ...state,
            freezeOwnerTeam,
            cmdbOwnerTeamId: freezeOwnerTeam
                ? state.ownerTeamId
                : state.cmdbOwnerTeamId,
            cmdbOwnerTeam: freezeOwnerTeam
                ? state.ownerTeam
                : state.cmdbOwnerTeam,
        }
    },
})

export const initialActionsState = {
    byId: {},
    uids: [],
    orphanedUids: [],
    // lastUid is the first available uid for when adding a new action
    lastUid: 1,
    ciToUid: {},
    actionLogs: {},
    topAction: null,
    loading: false,
    validation: null, // Initialize validation field
}

const actions = createReducer(initialActionsState, {
    [actionTypes.FOCUS_ACTION]: (state, action) => {
        let focused = action.actionId
        // TODO: maybe find cleaner way to clear filters
        for (let uid in state.byId) {
            state.byId[uid].filteredByUser = false
        }
        const expand = (uid) => {
            const act = state.byId[uid]
            if (act.treeChildren.length) {
                act.expanded = true
                act.treeChildren.forEach((a) => (state.byId[a].visible = true))
            }
            act.visible = true
            if (act.treeParent) {
                expand(act.treeParent)
            }
        }
        expand(focused)
        return {
            ...state,
            byId: { ...state.byId },
        }
    },
    [actionTypes.UPDATE_ACTION]: (state, action) => {
        // set children for new parents
        const { byId } = state
        const updatedById = {
            ...byId,
            [action.id]: runbookAction(byId[action.id], action),
        }

        // Apply auto-collapse logic after updating the action
        // Only runs if there's an active session (execution mode)
        applyAutoCollapse(
            [action.id],
            updatedById,
            action.runbookData && action.runbookData.activeSession !== null
        )

        return {
            ...state,
            byId: updatedById,
        }
    },
    [actionTypes.EDIT_RUNBOOK_ACTIONS]: (state, action) => {
        // set children for new parents
        const { byId } = state
        return {
            ...state,
            byId: {
                ...byId,
                [action.id]: runbookAction(byId[action.id], action),
            },
        }
    },
    [actionTypes.EXPAND_ACTION]: (state, action) => {
        const { byId } = state
        const actionId = action.actionId

        if (!byId[actionId]) {
            return state
        }

        // 1. Update the expanded state of the targeted action
        const act = {
            ...byId[actionId],
            expanded: !byId[actionId].expanded,
        }

        const updatedById = {
            ...byId,
            [actionId]: act,
        }

        // 2. Update visibility of children/descendants
        const affectedActions = getAffectedActions(actionId, updatedById, !act.expanded)
        affectedActions.forEach((uid) => {
            if (updatedById[uid]) {
                updatedById[uid] = {
                    ...updatedById[uid],
                    visible: act.expanded,
                }
            }
        })

        // 3. Re-calculate the display list (uids) if we have enough context
        // This ensures the view updates correctly when nodes are revealed/hidden
        const helper = new ActionsHelper({
            actions: {
                ...state,
                byId: updatedById,
            },
        })

        return {
            ...state,
            byId: updatedById,
            uids: helper.createActionsList(),
        }
    },
    [BULK_UPDATE_ACTIONS]: (state, action) => {
        const { byId } = state
        // we need to re-estimate the runbook when an update state action is done
        const ah = new ActionsHelper({
            actions: { ...state, byId: { ...byId, ...action.byId } },
            topAction: state.topAction,
            ...action.runbookData,
        })
        ah.startEstimateRunbook()
        // we validate the runbook on creation, so it's already estimated
        // Apply all updates from ActionsHelper (for estimation/validation)
        for (let uid in ah.actionsMap) {
            byId[uid] = runbookAction(byId[uid], {
                type: actionTypes.UPDATE_ACTION,
                data: ah.actionsMap[uid],
            })
        }

        // Only collect UIDs from the originally updated actions (not all from ah.actionsMap)
        // This ensures we only check the direct parent chain of changed actions
        const updatedActionUids = Object.keys(action.byId).map((uid) =>
            parseInt(uid)
        )

        // Apply auto-collapse logic after updating actions
        // Only runs if there's an active session (execution mode)
        applyAutoCollapse(
            updatedActionUids,
            byId,
            action.runbookData && action.runbookData.activeSession !== null
        )

        return {
            ...state,
            byId: { ...byId },
        }
    },
    [actionTypes.GET_RUNBOOK_RECEIVED]: (state, action) => {
        const byId = {},
            uids = []
        let lastUid = 1
        for (let act of action.data.actions) {
            byId[act.uid] = {
                ...initialActionState,
                ...act,
                visible: act.depth === 0,
            }
            uids.push(act.uid)
            lastUid = Math.max(lastUid, act.uid)
        }

        return {
            ...state,
            byId,
            uids,
            lastUid: lastUid + 1,
        }
    },
    [actionTypes.GET_ACTION_LOGS_SUCCESS]: (state, action) => {
        return {
            ...state,
            actionLogs: {
                ...state.actionLogs,
                [action.actionId]: action.payload.data.results.map((log) => ({
                    createdBy: log.created_by,
                    message: log.message,
                    logType: log.log_type,
                    id: log.id,
                    createdAt: moment(log.date_created).format(
                        TIMESTAMP_FORMAT
                    ),
                })),
            },
        }
    },
    [actionTypes.SET_DEPTH_LEVEL]: (state, action) => {
        const { selectedDepth } = action.payload
        for (let uid in state.byId) {
            state.byId[uid].expanded = selectedDepth > state.byId[uid].depth
            if (state.byId[uid].depth !== 0) {
                state.byId[uid].visible = selectedDepth >= state.byId[uid].depth
            }
        }
        return {
            ...state,
        }
    },
    [actionTypes.SAVE_SUPPLIER_SUCCESS]: (state, action) => {
        return {
            ...state,
            byId: {
                ...state.byId,
                [action.actionId]: runbookAction(
                    state.byId[action.actionId],
                    action
                ),
            },
        }
    },
    [actionTypes.TOGGLE_FREEZE_OWNER]: (state, action) => {
        return {
            ...state,
            byId: {
                ...state.byId,
                [action.actionId]: runbookAction(
                    state.byId[action.actionId],
                    action
                ),
            },
        }
    },
    [actionTypes.RUNBOOK_ACTIONS_LOADING]: (state) => {
        return { ...state, loading: true }
    },
})

export const addNewAction = (runbookId, data, options) => {
    /**
     * Adds a new action
     * @param {int}      runbookId   Id of runbook to add action to
     * @param {Object}   data        Initial action data to override default state
     * @param {Object}   [options]   Contains fromAction and parent (true: child | false: sibling).
     */
    return {
        type: actionTypes.ADD_NEW_ACTION,
        data,
        runbookId,
        options,
    }
}

export const addNewDevice = (runbookId, device, options) => {
    return {
        type: actionTypes.ADD_NEW_DEVICE,
        device,
        runbookId,
        options,
    }
}

export const addSolutionWithChildren = (
    runbookId,
    toAction,
    main,
    avZones,
    parent
) => {
    return {
        type: actionTypes.ADD_SOLUTION_WITH_CHILDREN,
        runbookId,
        toAction,
        main,
        avZones,
        parent,
    }
}

const dispatchNotifications = (runbookId, dispatch, getState) => {
    const runbook = getRunbook(getState(), runbookId)
    if (!runbook.isValid) {
        dispatch({
            type: actionTypes.TOAST_SHOW,
            message: {
                content: runbook.invalidMessage,
                intent: Intent.DANGER,
                icon: 'error',
            },
        })
    } else if (runbook.warnings?.length) {
        dispatch({
            type: actionTypes.TOAST_SHOW,
            message: {
                content: runbook.warnings.join('\n'),
                intent: Intent.WARNING,
                icon: 'error',
            },
        })
    }
}

export const updateAction = (runbookId, id, data) => (dispatch, getState) => {
    const runbook = getRunbook(getState(), runbookId)

    dispatch({
        type: actionTypes.UPDATE_ACTION,
        data, // {parents: []}
        id,
        runbookId,
        runbookData: {
            runbookStartTime: runbook.runbookStartTime,
            activeSession: runbook.activeSession,
        },
    })
    if (data.parents) {
        dispatchNotifications(runbookId, dispatch, getState)
    }
}

export const bulkUpdateActions = (runbookId, byId, reFilter = false) => (
    dispatch,
    getState
) => {
    const runbook = getRunbook(getState(), runbookId)

    // Track which actions are being updated and became successful
    const updatedToSuccess = []
    Object.keys(byId).forEach((uid) => {
        if (byId[uid].status === SUCCESS) {
            updatedToSuccess.push(parseInt(uid))
        }
    })

    dispatch({
        type: BULK_UPDATE_ACTIONS,
        runbookId,
        byId,
        runbookData: {
            runbookStartTime: runbook.runbookStartTime,
            activeSession: runbook.activeSession,
        },
    })

    // After bulk update, check if any parents should be auto-succeeded
    // This happens when all visible children of a parent are successful
    if (updatedToSuccess.length > 0 && runbook.activeSession) {
        const state = getState()
        const updatedRunbook = getRunbook(state, runbookId)
        const { byId: actionsById } = updatedRunbook.actions

        // Collect unique parents of actions that became successful
        const parentsToCheck = new Set()
        updatedToSuccess.forEach((uid) => {
            const action = actionsById[uid]
            if (action && action.treeParent) {
                parentsToCheck.add(action.treeParent)
            }
        })

        // Import asyncUpdateRunbookSession to avoid circular dependency
        const {
            asyncUpdateRunbookSession,
            collectHiddenActionUids,
        } = require('../index')

        // Check each parent and update if all visible children are successful
        const parentsToUpdate = []
        parentsToCheck.forEach((parentUid) => {
            const parent = actionsById[parentUid]
            if (!parent || parent.status === SUCCESS) {
                return
            }

            // Get visible children (not filtered)
            const visibleChildren = parent.treeChildren.filter((childUid) => {
                const child = actionsById[childUid]
                return child && !child.filteredByUser && !child.filteredByAction
            })

            // Check if all visible children are successful
            if (
                visibleChildren.length > 0 &&
                visibleChildren.every(
                    (childUid) => actionsById[childUid].status === SUCCESS
                )
            ) {
                parentsToUpdate.push(parentUid)
            }
        })

        // Update parents to SUCCESS
        if (parentsToUpdate.length > 0) {
            // Collect hidden action UIDs
            const hiddenActionUids = collectHiddenActionUids(actionsById)

            dispatch(
                asyncUpdateRunbookSession(
                    runbookId,
                    parentsToUpdate,
                    {
                        action: 'success',
                        propagate: false,
                    },
                    hiddenActionUids
                )
            )
        }
    }

    if (reFilter) {
        const state = getState()
        const runbook = getRunbook(state, state.ui.runbook.activeRunbook)
        if (!isEmpty(runbook.filterQuery)) {
            dispatch(filterActions(runbook.id, runbook.filterQuery))
        }
    }
}

export const moveAction = (runbookId, actionId, to, parent) => (
    dispatch,
    getState
) => {
    dispatch({
        type: actionTypes.MOVE_ACTION,
        actionId,
        runbookId,
        to,
        parent,
    })
    dispatchNotifications(runbookId, dispatch, getState)
}

export const moveActions = (
    runbookId,
    ids,
    { to, parent, firstChild, sortAlphabetically }
) => (dispatch, getState) => {
    /**
     * Takes a list of action uids and moves them one by one. last 3 arguments are in an object
     *   only one of the prevSibling/parent options should be provided
     * @param runbookId runbook id.
     * @param {Array} ids list of action uids.
     * @param prevSibling sibling after which we want to move the list of actions
     * @param parent parent which will be assigned to each action
     * @param {bool} firstChild only applies to parent,
     *   if true it will make ids[0] the first child of parent and all subsequent ids
     *   siblings of ids[0]
     **/
    dispatch({
        type: actionTypes.MOVE_ACTIONS,
        ids,
        runbookId,
        to,
        parent,
        firstChild,
        sortAlphabetically,
    })
    dispatchNotifications(runbookId, dispatch, getState)
}

export const getActionLogsFromServer = (runbookSessionId, actionId) => (
    dispatch,
    getState
) => {
    const { activeRunbook } = getState().ui.runbook
    return dispatch({
        type: actionTypes.API,
        method: 'get',
        payload: {
            url: `/api/runbooks/${runbookSessionId}/action-logs/${actionId}/`,
            start: () => ({ type: actionTypes.GET_ACTION_LOGS }),
            success: (payload) => ({
                type: actionTypes.GET_ACTION_LOGS_SUCCESS,
                payload,
                actionId,
                runbookId: activeRunbook,
            }),
            error: (error) => ({
                type: actionTypes.GET_ACTION_LOGS_ERROR,
                error,
                actionId,
                runbookId: activeRunbook,
            }),
        },
    })
}

export const addCommentToAction = (
    runbook_id,
    action_uid,
    comment,
    activeSession
) => (dispatch) => {
    return dispatch({
        type: actionTypes.API,
        method: 'post',
        data: { message: comment },
        payload: {
            url: `/api/runbooks/util/runbook/${runbook_id}/comment/${action_uid}/`,
            success: (payload) => {
                dispatch(getActionLogsFromServer(activeSession, action_uid))
            },
            error: (error) => {
                return {
                    type: actionTypes.TOAST_SHOW,
                    message: {
                        content:
                            'Something went wrong. Your comment was not added.',
                        intent: 'error',
                        icon: 'cross',
                    },
                }
            },
        },
    })
}

export const addNewSolution = (
    runbookId,
    actionId,
    solution,
    avZones,
    asChild
) => (dispatch, getState) => {
    // add new action for solution
    const lastUid = getLastUid(getState(), runbookId)
    dispatch(
        addNewDevice(runbookId, solution, {
            fromAction: actionId,
            parent: asChild,
        })
    )
    // trigger referesh solution
    dispatch(refreshSolution(runbookId, lastUid, { avZones, ascending: true }))
}

export const refreshSolution = (runbookId, actionId, params, copy) => (
    dispatch,
    getState
) => {
    const state = getState()
    const action = getAction(state, runbookId, actionId)
    const runbook = getState().runbooks.byId[runbookId]
    dispatch({
        type: actionTypes.API,
        method: 'get',
        payload: {
            url: `${SOLUTIONS_URL}${
                action ? action.customerId : runbook.orgPk
            }/`,
            start: () => ({
                type: actionTypes.REFRESH_SOLUTION,
                runbookId,
                actionId,
            }),
            success: (payload) => {
                const response = {
                    type: actionTypes.REFRESH_SOLUTION_SUCCESS,
                    payload: payload.data.results,
                    runbookId,
                    actionId,
                    params,
                }
                return response
            },
            error: (error) => {
                const message =
                    'Failed to refresh solution. Error: ' + error.message
                dispatchErrorToast(dispatch, message)
                return {
                    type: actionTypes.REFRESH_SOLUTION_ERROR,
                    name: error.name,
                    message: error.message,
                }
            },
        },
    })
}

export const deleteAction = (runbookId, id, withChildren) => (dispatch) => {
    dispatch({ type: actionTypes.DELETE_ACTION_START })
    dispatch({
        type: actionTypes.DELETE_ACTION,
        id,
        runbookId,
        withChildren,
    })
    dispatch({ type: actionTypes.DELETE_ACTION_END })
}

export const filterActions = (runbookId, query) => {
    return {
        type: actionTypes.FILTER_RUNBOOK_ACTIONS,
        runbookId,
        query,
    }
}

export const focusAction = (runbookId, actionId) => (dispatch) => {
    setTimeout(() => {
        const domElement = document.getElementById(`${actionId}-card`)
        if (!domElement) {
            return
        }
        domElement.classList.add('fade')
        setTimeout(() => {
            domElement.classList.remove('fade')
        }, 1000)
    }, 500)

    dispatch({
        type: actionTypes.FOCUS_ACTION,
        runbookId,
        actionId,
    })
    // setTimeout(() => {
    //     const el = document.getElementById(`${actionId}-card`)
    //     if (el) {
    //         document.getElementById(`${actionId}-card`).scrollIntoView({
    //             behavior: 'smooth',
    //             block: 'center',
    //         })
    //         el.classList.add('fade')
    //         setTimeout(() => {
    //             el.classList.remove('fade')
    //         }, 1000)
    //     }
    // }, 300)
}

export const markActionInfoOnly = (runbookId, actionId) => {
    return {
        type: actionTypes.MARK_INFO_ONLY,
        runbookId,
        actionId,
    }
}

export const toggleFreezeOwner = (runbookId, actionId) => ({
    type: actionTypes.TOGGLE_FREEZE_OWNER,
    runbookId,
    actionId,
})

export const expandAction = (runbookId, actionId) => ({
    type: actionTypes.EXPAND_ACTION,
    runbookId,
    actionId,
})

export const startActionsLoading = (runbookId) => ({
    type: actionTypes.RUNBOOK_ACTIONS_LOADING,
    runbookId,
})

export const getAction = (state, runbookId, id) => {
    if (!id) return null
    const runbook = state.runbooks.byId[runbookId]
    if (!runbook || !runbook.actions) return {}

    const action = runbook.actions.byId[id]
    if (!action) return {}

    const actionLogs = runbook.actions.actionLogs || {}
    action.logs = actionLogs[action.uid] || []

    return action
}

export const getActiveAction = (state, id) =>
    getAction(state, state.ui.runbook.activeRunbook, id)

export const getActionDevice = (state, runbookId, id) => {
    const action = getAction(state, runbookId, id)
    if (!action) return null
    if (action.device) {
        return state.runbooks.byId[runbookId].actions.ciToUid[action.device]
    }
    return null
}

export const isDevice = (action) =>
    action.deviceClass &&
    action.deviceClass !== 'DR Solution' &&
    action.deviceClass !== 'Business Solution'

export const isVirp = (action) =>
    action.deviceClass === 'Virtual Infrastructure Recovery Plan'

export const isSolution = (action) =>
    action.deviceClass &&
    (action.deviceClass === 'DR Solution' ||
        action.deviceClass === 'Business Solution')

export const getTopAction = (state, runbookId) => {
    const firstList = Object.entries(
        state.runbooks.byId[runbookId].actions.byId
    ).filter((arr) => arr[1].depth === 0 && arr[1].prevSibling === null)
    return firstList.length
        ? getAction(state, runbookId, firstList[0][0])
        : null
}

export const getSelectedActions = (state, runbookId) => {
    const actions = state.runbooks.byId[runbookId].actions
    if (!actions) {
        return []
    }
    return actions.uids.filter(
        (uid) => actions.byId[uid] && actions.byId[uid].selected
    )
}

export const getVisibleActionUids = (state, runbookId) => {
    // Safety check: return empty array if runbook doesn't exist
    const runbook = state.runbooks.byId[runbookId]
    if (!runbook) {
        return []
    }

    const actions = runbook.actions
    const filterQuery = runbook.filterQuery
    const { byId, uids } = actions

    // If there is any filter applied, return all non-filtered UIDs
    if (Object.keys(filterQuery).length > 0) {
        return uids.filter((uid) => {
            const item = byId[uid]
            return item && !item.filteredByUser && !item.filteredByAction
        })
    }

    // When no filter is applied
    return uids.filter((uid) => {
        const item = byId[uid]

        if (
            !item ||
            item.filteredByUser ||
            item.filteredByAction ||
            !item.visible
        ) {
            return false
        }

        const hasFilters = Object.keys(item.filters || {}).length > 0

        if (!hasFilters) {
            return true
        }

        const visibleChildCount = (item.children ?? []).filter((childUUID) => {
            const child = byId[childUUID]
            return child && !child.filteredByUser && !child.filteredByAction
        }).length

        return visibleChildCount > 0
    })
}

export const getVisibleOrphans = (state, runbookId) => {
    const { byId, orphanedUids } = state.runbooks.byId[runbookId].actions
    return orphanedUids.filter(
        (uid) =>
            !byId[uid].filteredByUser &&
            !byId[uid].filteredByAction &&
            byId[uid].visible
    )
}

export const getLastUid = (state, runbookId) =>
    state.runbooks.byId[runbookId].actions.lastUid

export default actions
